import * as THREE from 'three';
import { loadGlb } from '../../../assets/loadGlb';
import type { PhysicsWorld } from '../../../physics/PhysicsWorld';
import { addOcean, type Ocean } from '../../Ocean';
import { addSky } from '../../Sky';

export type CrashCoastPoint = { x: number; y: number; z: number };

export type CrashCoastBuild = {
  spawn: CrashCoastPoint;
  nodes: Record<string, CrashCoastPoint>;
  ocean: Ocean;
};

/** Local slice: origin on the sand, +Z ocean / south, +X east. */
export const CC_SPAWN: CrashCoastPoint = { x: 0, y: 1.0, z: 8 };

/** Inland palms. Shared with gather + trunk colliders. */
export const CC_PALMS: { x: number; z: number; h: number }[] = [
  { x: -28, z: -26, h: 7.4 },
  { x: -32, z: -34, h: 8.1 },
  { x: -24, z: -38, h: 6.8 },
  { x: -36, z: -42, h: 7.6 },
  { x: -30, z: -48, h: 7.9 },
  { x: -22, z: -52, h: 6.5 },
  { x: -34, z: -56, h: 8.4 },
  { x: -26, z: -64, h: 7.2 },
  { x: -31, z: -72, h: 8.0 },
  { x: -23, z: -78, h: 6.9 },
  { x: -29, z: -88, h: 8.2 },
  { x: -20, z: -84, h: 7.0 },
  { x: 22, z: -24, h: 7.1 },
  { x: 27, z: -28, h: 6.4 },
  { x: 28, z: -32, h: 8.0 },
  { x: 18, z: -38, h: 6.6 },
  { x: 33, z: -40, h: 7.3 },
  { x: 26, z: -46, h: 7.8 },
  { x: 4, z: -34, h: 6.5 },
  { x: 32, z: -54, h: 8.3 },
  { x: 20, z: -58, h: 6.9 },
  { x: 2, z: -50, h: 7.4 },
  { x: 8, z: -62, h: 7.6 },
  { x: 29, z: -66, h: 7.5 },
  { x: 16, z: -72, h: 6.7 },
  { x: 24, z: -80, h: 8.1 },
  { x: 6, z: -48, h: 6.8 },
  { x: -2, z: -82, h: 8.0 },
  { x: 21, z: -88, h: 7.7 },
  { x: -12, z: -88, h: 7.2 },
  { x: 10, z: -90, h: 7.8 },
  { x: 8, z: -21, h: 6.2 },
];

export const CC_NODES: Record<string, CrashCoastPoint> = {
  'CC-COCKPIT': CC_SPAWN,
  'CC-TAIL': { x: 0, y: 1.2, z: -12 },
  'CC-CABIN': { x: -8, y: 1.6, z: -22 },
  'CC-EMBER': { x: -18, y: 0.4, z: -40 },
  'CC-FIG': { x: -8, y: 1, z: -70 },
  'CE-PAD': { x: -18, y: 0.05, z: -40 },
  'CC-BEACON': { x: 10, y: 0.9, z: 10 },
};

type CoastMats = {
  sand: THREE.MeshStandardMaterial;
  wetSand: THREE.MeshStandardMaterial;
  wreck: THREE.MeshStandardMaterial;
  bark: THREE.MeshStandardMaterial;
  fern: THREE.MeshStandardMaterial;
  rock: THREE.MeshStandardMaterial;
  hide: THREE.MeshStandardMaterial;
  glass: THREE.MeshStandardMaterial;
  ember: THREE.MeshStandardMaterial;
  soot: THREE.MeshStandardMaterial;
};

function loadMap(url: string, rx: number, ry: number): THREE.Texture {
  const tex = new THREE.TextureLoader().load(url);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.repeat.set(rx, ry);
  tex.anisotropy = 8;
  return tex;
}

function makeMats(): CoastMats {
  return {
    sand: new THREE.MeshStandardMaterial({
      color: 0xd2c09a,
      map: loadMap('/tex/sand.jpg', 8, 14),
      roughness: 0.96,
      metalness: 0.02,
    }),
    wetSand: new THREE.MeshStandardMaterial({
      color: 0xb49870,
      map: loadMap('/tex/sand.jpg', 4, 2),
      roughness: 0.62,
      metalness: 0.04,
    }),
    wreck: new THREE.MeshStandardMaterial({
      color: 0x9a9084,
      map: loadMap('/tex/wreck.jpg', 2, 2),
      roughness: 0.72,
      metalness: 0.28,
    }),
    bark: new THREE.MeshStandardMaterial({
      color: 0x8a6a48,
      map: loadMap('/tex/bark.jpg', 1, 3),
      roughness: 0.92,
      metalness: 0.02,
    }),
    fern: new THREE.MeshStandardMaterial({
      color: 0xc8d2a8,
      map: loadMap('/tex/fern.jpg', 1, 1),
      roughness: 0.78,
      metalness: 0.02,
      side: THREE.DoubleSide,
      transparent: true,
      alphaTest: 0.32,
    }),
    rock: new THREE.MeshStandardMaterial({
      color: 0x8a8478,
      map: loadMap('/tex/rock.jpg', 2, 2),
      roughness: 0.96,
      metalness: 0.04,
    }),
    hide: new THREE.MeshStandardMaterial({
      color: 0x8a6e52,
      map: loadMap('/tex/hide.jpg', 1, 1),
      roughness: 0.88,
      metalness: 0.04,
    }),
    glass: new THREE.MeshStandardMaterial({
      color: 0x1a2830,
      roughness: 0.18,
      metalness: 0.42,
      transparent: true,
      opacity: 0.55,
    }),
    ember: new THREE.MeshStandardMaterial({
      color: 0x3a1810,
      emissive: 0xff5a18,
      emissiveIntensity: 0.35,
      roughness: 0.55,
      metalness: 0.08,
    }),
    soot: new THREE.MeshStandardMaterial({
      color: 0x2a221c,
      roughness: 0.94,
      metalness: 0.06,
    }),
  };
}

/**
 * Textured Crash Coast. Integrator calls this after `new PhysicsWorld()` and
 * teleports the capsule to `spawn`. Pickup IDs stay locked.
 */
export function buildCrashCoast(scene: THREE.Scene, physics: PhysicsWorld): CrashCoastBuild {
  addSky(scene);

  const root = new THREE.Group();
  tag(root, 'Crash Coast');
  root.userData.area = 'crash_coast';
  scene.add(root);

  const mats = makeMats();
  const ocean = addOcean(root);

  addIslandBounds(physics);
  addSand(root, mats);
  addWreck(root, physics, mats);
  addRocks(root, physics, mats);
  addCliffs(root, physics, mats);
  addPalms(root, physics, mats);
  addFerns(root, mats);
  addEmber(root, physics, mats);
  addFigGate(root, physics, mats);
  addBeacon(root, mats);

  return {
    spawn: { x: 0, y: 1.0, z: 8 },
    nodes: {
      'CC-COCKPIT': { x: 0, y: 1.0, z: 8 },
      'CC-TAIL': { x: 0, y: 1.2, z: -12 },
      'CC-CABIN': { x: -8, y: 1.6, z: -22 },
      'CC-EMBER': { x: -18, y: 0.4, z: -40 },
      'CC-FIG': { x: -8, y: 1, z: -70 },
      'CE-PAD': { x: -18, y: 0.05, z: -40 },
      'CC-BEACON': { x: 10, y: 0.9, z: 10 },
    },
    ocean,
  };
}

function addIslandBounds(physics: PhysicsWorld): void {
  const wallH = 10;
  const wallY = wallH * 0.5;
  physics.addStaticCuboid(2, wallH, 320, 268, wallY, -40);
  physics.addStaticCuboid(2, wallH, 320, -268, wallY, -40);
  physics.addStaticCuboid(280, wallH, 2, 0, wallY, 72);
  physics.addStaticCuboid(280, wallH, 2, 0, wallY, -308);
}

function addSand(root: THREE.Group, mats: CoastMats): void {
  const geo = new THREE.PlaneGeometry(360, 520, 48, 64);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position;
  if (pos) {
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      let y =
        Math.sin(x * 0.05 + z * 0.018) * 0.1 +
        Math.sin(x * 0.13 - z * 0.08) * 0.045 +
        Math.sin(z * 0.035) * 0.06;
      const nearPath = Math.min(
        Math.hypot(x, z - 8),
        Math.hypot(x + 8, z + 22),
        Math.hypot(x + 18, z + 40),
        Math.hypot(x + 8, z + 70),
      );
      if (nearPath < 10) {
        y *= nearPath / 10 * 0.2;
      }
      pos.setY(i, 0.02 + y);
    }
    pos.needsUpdate = true;
  }
  geo.computeVertexNormals();
  const sand = new THREE.Mesh(geo, mats.sand);
  sand.receiveShadow = true;
  tag(sand, 'CC-SAND');
  root.add(sand);

  const dirt = new THREE.Mesh(new THREE.PlaneGeometry(220, 260), mats.rock);
  dirt.rotation.x = -Math.PI / 2;
  dirt.position.set(-8, 0.018, -150);
  dirt.receiveShadow = true;
  tag(dirt, 'CC-INLAND');
  root.add(dirt);

  const wet = new THREE.Mesh(new THREE.PlaneGeometry(90, 14), mats.wetSand);
  wet.rotation.x = -Math.PI / 2;
  wet.position.set(0, 0.034, 12.4);
  wet.receiveShadow = true;
  tag(wet, 'CC-WET-SAND');
  root.add(wet);

  const dunes: [number, number, number, number, number][] = [
    [0, -6, 18, 0.06, 0.04],
    [12, -18, 14, 0.09, -0.05],
    [-14, -14, 16, 0.07, 0.06],
    [8, 2, 11, 0.05, 0.03],
    [-10, 4, 10, 0.05, -0.04],
    [-22, -32, 13, 0.08, 0.05],
    [18, -36, 12, 0.07, -0.03],
    [-6, -48, 15, 0.06, 0.02],
    [6, -8, 9, 0.045, 0.05],
    [-18, -8, 8, 0.05, -0.06],
  ];
  for (let i = 0; i < dunes.length; i++) {
    const d = dunes[i];
    if (!d) {
      continue;
    }
    const [x, z, r, y, tilt] = d;
    const disk = new THREE.Mesh(new THREE.CircleGeometry(r, 18), mats.sand);
    disk.rotation.x = -Math.PI / 2 + tilt;
    disk.position.set(x, y, z);
    disk.receiveShadow = true;
    tag(disk, `CC-DUNE-${String(i + 1).padStart(2, '0')}`);
    root.add(disk);
  }
}

function addWreck(root: THREE.Group, physics: PhysicsWorld, mats: CoastMats): void {
  // Nose sits ocean-side of spawn so the capsule is not inside the hull.
  const cockpit = new THREE.Group();
  tag(cockpit, 'CC-COCKPIT');
  cockpit.position.set(0.35, 1.05, 12.15);
  cockpit.rotation.set(0.12, 0.18, 0.22);
  root.add(cockpit);

  const nose = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.92, 3.6, 12), mats.wreck);
  nose.rotation.x = Math.PI / 2;
  nose.castShadow = true;
  nose.receiveShadow = true;
  cockpit.add(nose);

  const cone = new THREE.Mesh(new THREE.ConeGeometry(0.42, 1.15, 10), mats.wreck);
  cone.rotation.x = Math.PI / 2;
  cone.position.z = 2.25;
  cone.castShadow = true;
  cockpit.add(cone);

  const glass = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.55, 0.85), mats.glass);
  glass.position.set(0, 0.42, 0.35);
  cockpit.add(glass);

  const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.22, 8), mats.soot);
  hub.rotation.x = Math.PI / 2;
  hub.position.z = 2.82;
  cockpit.add(hub);
  const blade = new THREE.Mesh(new THREE.BoxGeometry(0.08, 1.85, 0.1), mats.soot);
  blade.position.z = 2.88;
  blade.rotation.z = 0.4;
  cockpit.add(blade);

  const stubL = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.1, 0.7), mats.wreck);
  stubL.position.set(-1.6, -0.05, 0.1);
  stubL.rotation.z = 0.12;
  stubL.castShadow = true;
  cockpit.add(stubL);
  const stubR = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.1, 0.55), mats.wreck);
  stubR.position.set(1.15, -0.15, -0.2);
  stubR.rotation.set(0.1, 0, -0.35);
  cockpit.add(stubR);

  physics.addStaticCuboid(0.95, 0.85, 2.05, 0.35, 1.05, 12.15);
  void dressCockpit(cockpit);

  const tail = new THREE.Group();
  tag(tail, 'CC-TAIL');
  tail.position.set(0, 1.15, -12);
  tail.rotation.set(0.08, -0.22, -0.18);
  root.add(tail);

  const tailTube = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.72, 3.4, 11), mats.wreck);
  tailTube.rotation.x = Math.PI / 2;
  tailTube.castShadow = true;
  tailTube.receiveShadow = true;
  tail.add(tailTube);

  const fin = new THREE.Mesh(new THREE.BoxGeometry(0.12, 2.05, 1.15), mats.wreck);
  fin.position.set(0, 1.25, -0.85);
  fin.castShadow = true;
  tail.add(fin);

  const stab = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.1, 0.7), mats.wreck);
  stab.position.set(0, 0.55, -1.05);
  stab.castShadow = true;
  tail.add(stab);

  physics.addStaticCuboid(0.75, 1.15, 1.7, 0, 1.2, -12);

  const cabin = new THREE.Group();
  tag(cabin, 'CC-CABIN');
  cabin.position.set(-8, 1.55, -22);
  cabin.rotation.set(0.05, 0.55, 0.16);
  root.add(cabin);

  const mid = new THREE.Mesh(new THREE.CylinderGeometry(1.05, 1.12, 6.4, 12), mats.wreck);
  mid.rotation.x = Math.PI / 2;
  mid.castShadow = true;
  mid.receiveShadow = true;
  cabin.add(mid);

  const rip = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 1.08, 1.4, 10, 1, true), mats.soot);
  rip.rotation.x = Math.PI / 2;
  rip.position.z = 3.4;
  cabin.add(rip);

  const wing = new THREE.Mesh(new THREE.BoxGeometry(7.4, 0.12, 1.45), mats.wreck);
  wing.position.set(-0.4, 0.15, 0.2);
  wing.rotation.z = -0.08;
  wing.castShadow = true;
  wing.receiveShadow = true;
  cabin.add(wing);

  const flap = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.08, 0.55), mats.wreck);
  flap.position.set(3.4, -0.35, 0.8);
  flap.rotation.set(0.4, 0.2, 0.6);
  cabin.add(flap);

  physics.addStaticCuboid(2.1, 1.15, 3.2, -8, 1.55, -22);
  void dressCabinHero(cabin, mid);

  const strayWing = new THREE.Mesh(new THREE.BoxGeometry(5.6, 0.12, 1.25), mats.wreck);
  strayWing.position.set(4.6, 0.18, -9.4);
  strayWing.rotation.set(0.05, 0.7, 0.12);
  strayWing.castShadow = true;
  strayWing.receiveShadow = true;
  tag(strayWing, 'CC-WING-STRAY');
  root.add(strayWing);
  physics.addStaticCuboid(2.6, 0.12, 0.55, 4.6, 0.18, -9.4);

  const bags: [number, number, number, number, number, number, boolean][] = [
    [1.6, 0.18, 10.2, 0.35, 0.16, 0.28, false],
    [2.4, 0.2, 9.1, 0.42, 0.2, 0.32, true],
    [-2.1, 0.16, 10.8, 0.3, 0.14, 0.24, false],
    [-6.2, 0.22, -19.4, 0.38, 0.2, 0.3, true],
    [-9.6, 0.18, -24.2, 0.34, 0.16, 0.26, false],
    [1.2, 0.15, -10.6, 0.28, 0.14, 0.22, false],
    [-1.8, 0.17, -13.4, 0.4, 0.18, 0.3, true],
  ];
  for (let i = 0; i < bags.length; i++) {
    const b = bags[i];
    if (!b) {
      continue;
    }
    const [x, y, z, hx, hy, hz, hide] = b;
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(hx * 2, hy * 2, hz * 2), hide ? mats.hide : mats.wreck);
    mesh.position.set(x, y, z);
    mesh.rotation.y = i * 0.7;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    tag(mesh, `CC-BAG-${String(i + 1).padStart(2, '0')}`);
    root.add(mesh);
    physics.addStaticCuboid(hx, hy, hz, x, y, z);
  }
}

function addRocks(root: THREE.Group, physics: PhysicsWorld, mats: CoastMats): void {
  const specs: [string, number, number, number, number, number, number][] = [
    ['CC-ROCK-01', 6, 0.55, -6, 1.15, 0.85, 1.0],
    ['CC-ROCK-02', 7.5, 0.38, -4.8, 0.75, 0.55, 0.65],
    ['CC-ROCK-03', 4.8, 0.32, -7.1, 0.58, 0.48, 0.7],
    ['CC-ROCK-04', -5.4, 0.4, 5.2, 0.9, 0.6, 0.8],
    ['CC-ROCK-05', 11, 0.5, -16, 1.2, 0.75, 1.1],
    ['CC-ROCK-06', -12.5, 0.45, -28, 1.05, 0.7, 0.9],
    ['CC-ROCK-07', 3.2, 0.35, -33, 0.8, 0.55, 0.7],
    ['CC-ROCK-08', -24, 0.55, -46, 1.3, 0.85, 1.15],
  ];
  for (const spec of specs) {
    const [id, x, y, z, sx, sy, sz] = spec;
    const mesh = new THREE.Mesh(new THREE.IcosahedronGeometry(0.55, 1), mats.rock);
    mesh.scale.set(sx, sy, sz);
    mesh.position.set(x, y, z);
    mesh.rotation.set(0.3, x * 0.2, 0.15);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    tag(mesh, id);
    root.add(mesh);
    physics.addStaticCuboid(sx * 0.4, sy * 0.45, sz * 0.4, x, y, z);
  }
}

function addCliffs(root: THREE.Group, physics: PhysicsWorld, mats: CoastMats): void {
  const slabs: [string, number, number, number, number, number, number][] = [
    ['CC-CLIFF-E0', 40, 5.5, -8, 6, 8, 18],
    ['CC-CLIFF-E1', 42, 7, -42, 7, 10, 22],
    ['CC-CLIFF-E2', 39, 6.2, -78, 6.5, 9, 16],
    ['CC-CLIFF-W0', -40, 5.8, -6, 6.2, 8.4, 17],
    ['CC-CLIFF-W1', -42, 7.2, -40, 7.4, 10.5, 22],
    ['CC-CLIFF-W2', -39, 6.4, -76, 6.4, 9.2, 16],
  ];
  for (const spec of slabs) {
    const [id, x, y, z, sx, sy, sz] = spec;
    const mesh = new THREE.Mesh(new THREE.IcosahedronGeometry(1, 1), mats.rock);
    mesh.scale.set(sx, sy, sz);
    mesh.position.set(x, y, z);
    mesh.rotation.y = x > 0 ? 0.2 : -0.18;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    tag(mesh, id);
    root.add(mesh);
    physics.addStaticCuboid(sx * 0.55, sy * 0.7, sz * 0.55, x, y, z);
  }
}

function addPalms(root: THREE.Group, physics: PhysicsWorld, mats: CoastMats): void {
  const trees = CC_PALMS;
  const count = trees.length;
  const trunkGeo = new THREE.CylinderGeometry(0.16, 0.3, 1, 7);
  const trunks = new THREE.InstancedMesh(trunkGeo, mats.bark, count);
  trunks.name = 'CC-PALM-TRUNKS';
  trunks.castShadow = true;
  trunks.receiveShadow = true;
  root.add(trunks);

  const frondsPer = 7;
  const frondGeo = new THREE.PlaneGeometry(2.6, 1.15);
  const fronds = new THREE.InstancedMesh(frondGeo, mats.fern, count * frondsPer);
  fronds.name = 'CC-PALM-FRONDS';
  fronds.castShadow = false;
  root.add(fronds);

  const dummy = new THREE.Object3D();
  let fi = 0;
  for (let i = 0; i < count; i++) {
    const tree = trees[i];
    if (!tree) {
      continue;
    }
    const { x, z, h } = tree;
    dummy.position.set(x, h * 0.5, z);
    dummy.rotation.set(0.04, x * 0.3, 0.03);
    dummy.scale.set(1, h, 1);
    dummy.updateMatrix();
    trunks.setMatrixAt(i, dummy.matrix);

    physics.addStaticCylinder(h * 0.5, 0.42, x, h * 0.5, z);

    for (let f = 0; f < frondsPer; f++) {
      const yaw = (f / frondsPer) * Math.PI * 2 + i * 0.15;
      dummy.position.set(x + Math.sin(yaw) * 0.35, h - 0.15, z + Math.cos(yaw) * 0.35);
      dummy.rotation.set(0.85, yaw, 0.15);
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();
      fronds.setMatrixAt(fi, dummy.matrix);
      fi += 1;
    }
  }
  trunks.instanceMatrix.needsUpdate = true;
  fronds.instanceMatrix.needsUpdate = true;
  trunks.computeBoundingSphere();
  fronds.computeBoundingSphere();
  void dressPalms(root, trunks, fronds);
}

const PALM_GLB = '/models/veg/veg_palm.glb';
const PALM_CANON_H = 7.4;

async function dressPalms(
  root: THREE.Group,
  trunks: THREE.InstancedMesh,
  fronds: THREE.InstancedMesh,
): Promise<void> {
  let proto: THREE.Group;
  try {
    proto = await loadGlb(PALM_GLB);
  } catch {
    return;
  }
  proto.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      obj.castShadow = true;
      obj.receiveShadow = true;
    }
  });
  const dressed = new THREE.Group();
  dressed.name = 'CC-PALM-GLB';
  for (const tree of CC_PALMS) {
    const clone = proto.clone(true);
    const scale = tree.h / PALM_CANON_H;
    clone.position.set(tree.x, 0, tree.z);
    clone.scale.setScalar(scale);
    clone.rotation.y = tree.x * 0.3;
    dressed.add(clone);
  }
  root.add(dressed);
  trunks.visible = false;
  fronds.visible = false;
}

function addFerns(root: THREE.Group, mats: CoastMats): void {
  const clumps: [number, number][] = [
    [-14, -28],
    [-10, -32],
    [5, -30],
    [12, -36],
    [-20, -44],
    [-6, -46],
    [9, -52],
    [-16, -58],
    [3, -64],
    [-12, -66],
    [14, -70],
    [-4, -74],
    [7, -78],
    [-18, -80],
    [11, -84],
    [-9, -36],
    [16, -44],
    [-22, -62],
  ];
  const card = new THREE.PlaneGeometry(1.15, 0.95);
  for (let i = 0; i < clumps.length; i++) {
    const c = clumps[i];
    if (!c) {
      continue;
    }
    const [x, z] = c;
    const g = new THREE.Group();
    tag(g, `CC-FERN-${String(i + 1).padStart(2, '0')}`);
    g.position.set(x, 0.42, z);
    for (let k = 0; k < 3; k++) {
      const leaf = new THREE.Mesh(card, mats.fern);
      leaf.position.set((k - 1) * 0.22, 0, (k % 2) * 0.12);
      leaf.rotation.y = k * 0.9 + i * 0.2;
      g.add(leaf);
    }
    root.add(g);
  }
}

function addEmber(root: THREE.Group, physics: PhysicsWorld, mats: CoastMats): void {
  const ember = new THREE.Group();
  tag(ember, 'Camp Ember');
  root.add(ember);

  const pad = new THREE.Mesh(new THREE.CircleGeometry(6.2, 20), mats.wetSand);
  pad.rotation.x = -Math.PI / 2;
  pad.position.set(-18, 0.03, -40);
  pad.receiveShadow = true;
  tag(pad, 'CE-PAD');
  ember.add(pad);

  const fire = new THREE.Group();
  fire.name = 'Camp Ember Fire';
  fire.position.set(-18, 0.4, -40);
  ember.add(fire);

  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const stone = new THREE.Mesh(new THREE.IcosahedronGeometry(0.22, 0), mats.rock);
    stone.position.set(Math.cos(a) * 0.85, -0.18, Math.sin(a) * 0.85);
    stone.scale.set(1.1, 0.7, 0.95);
    stone.castShadow = true;
    fire.add(stone);
  }

  const logA = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.11, 1.05, 6), mats.soot);
  logA.rotation.z = Math.PI / 2;
  logA.rotation.y = 0.3;
  logA.position.y = -0.08;
  fire.add(logA);
  const logB = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.1, 0.95, 6), mats.soot);
  logB.rotation.z = Math.PI / 2;
  logB.rotation.y = -0.8;
  logB.position.y = -0.02;
  fire.add(logB);

  // Mesh id so CampDirector can punch emissive when the fire is lit.
  const coal = new THREE.Mesh(new THREE.IcosahedronGeometry(0.2, 0), mats.ember);
  coal.position.y = -0.12;
  coal.scale.set(1.2, 0.55, 1.1);
  tag(coal, 'CC-EMBER');
  fire.add(coal);

  physics.addStaticCylinder(0.4, 0.55, -18, 0.4, -40);
}

function addFigGate(root: THREE.Group, physics: PhysicsWorld, mats: CoastMats): void {
  const fig = new THREE.Group();
  tag(fig, 'CC-FIG');
  root.add(fig);

  addFigTrunk(fig, physics, mats, 'CC-FIG-L', -10.2, -70);
  addFigTrunk(fig, physics, mats, 'CC-FIG-R', -5.8, -70);

  const canopy = new THREE.PlaneGeometry(7.2, 5.4);
  for (let i = 0; i < 6; i++) {
    const card = new THREE.Mesh(canopy, mats.fern);
    card.position.set(-8 + (i % 2) * 0.6, 6.4 + (i % 3) * 0.25, -70 + (i - 2) * 0.35);
    card.rotation.set(-0.55, i * 0.35, 0.08);
    fig.add(card);
  }
}

function addBeacon(root: THREE.Group, mats: CoastMats): void {
  const at = CC_NODES['CC-BEACON'] ?? { x: 10, y: 0.9, z: 10 };
  const group = new THREE.Group();
  tag(group, 'CC-BEACON');
  group.position.set(at.x, 0, at.z);
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.07, 2.4, 6), mats.wreck);
  pole.position.y = 1.2;
  const lamp = new THREE.Mesh(
    new THREE.SphereGeometry(0.12, 8, 6),
    new THREE.MeshStandardMaterial({
      color: 0xc8d8e0,
      emissive: 0x6a9aaa,
      emissiveIntensity: 1.4,
      roughness: 0.35,
      metalness: 0.2,
    }),
  );
  lamp.position.y = 2.45;
  group.add(pole, lamp);
  root.add(group);
}

function addFigTrunk(
  parent: THREE.Object3D,
  physics: PhysicsWorld,
  mats: CoastMats,
  id: string,
  x: number,
  z: number,
): void {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.85, 6.2, 8), mats.bark);
  mesh.position.set(x, 3.1, z);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  tag(mesh, id);
  parent.add(mesh);
  physics.addStaticCylinder(3.1, 0.7, x, 3.1, z);
}

function tag(obj: THREE.Object3D, id: string): void {
  obj.name = id;
  obj.userData.id = id;
}

async function dressCockpit(root: THREE.Group): Promise<void> {
  try {
    const hero = await loadGlb('/models/env/env_cockpit.glb');
    hero.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.castShadow = true;
        obj.receiveShadow = true;
      }
    });
    for (const child of root.children) {
      child.visible = false;
    }
    root.add(hero);
  } catch {
    /* keep primitive nose */
  }
}

/** Swap the placeholder cabin tube for the authored Blender hull when the GLB loads. */
async function dressCabinHero(cabin: THREE.Group, placeholder: THREE.Object3D): Promise<void> {
  try {
    const hero = await loadGlb('/models/env/env_wreck_fuselage.glb');
    hero.traverse((obj) => {
      obj.castShadow = true;
      obj.receiveShadow = true;
    });
    hero.rotation.x = -Math.PI / 2;
    hero.position.y = -1.55;
    cabin.add(hero);
    placeholder.visible = false;
  } catch {
    placeholder.visible = true;
  }
}
